/**
 * Investigation Prompts Queue (R4.4 / LA-011).
 *
 * Adam UI surface for dev-loop investigation prompts queued for operator
 * review. Implements Option A from the plan: queue + clipboard-copy
 * affordance. The "execute via Claude Code" button is deferred — it
 * requires operator-approval scope to auto-invoke a Claude prompt.
 *
 * Reads `/api/learning/investigation-prompts` (Adam proxies to
 * learning-hub `/api/dev-loop/investigation-prompts`). Mutates via
 * PATCH to mark prompts reviewed or discarded.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  PageHeader,
  Card,
  SectionHeader,
  Badge,
  Button,
  Spinner,
  QueryError,
  EmptyState,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPatch } from "../lib/api";

type Severity = "low" | "medium" | "high";

interface InvestigationPrompt {
  id: string;
  deploymentId: string;
  component: string;
  severity: Severity;
  description: string;
  claudePrompt: string;
  filePaths: string[];
  testCommand: string | null;
  dedupKey: string | null;
  status: string;
  createdAt: number;
  reviewedAt: number | null;
  reviewedBy: string | null;
  reviewerNote: string | null;
}

interface PromptsListResponse {
  prompts: InvestigationPrompt[];
  timestamp: number;
}

const SEVERITY_VARIANT: Record<Severity, "danger" | "warning" | "info"> = {
  high: "danger",
  medium: "warning",
  low: "info",
};

export function InvestigationPromptsQueue(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery<PromptsListResponse>({
    queryKey: ["learning", "investigation-prompts"],
    queryFn: () => apiGet<PromptsListResponse>("/api/learning/investigation-prompts"),
  });
  usePolling(refetch, 60_000);

  const markReviewed = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "reviewed" | "discarded" }) =>
      apiPatch<{ ok: boolean }>(`/api/learning/investigation-prompts/${id}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["learning", "investigation-prompts"] });
    },
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Investigation Prompts Queue" description="Dev-loop prompts awaiting review." />
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <PageHeader title="Investigation Prompts Queue" description="Dev-loop prompts awaiting review." />
        <QueryError error={error as Error} retry={refetch} />
      </div>
    );
  }
  const prompts = data?.prompts ?? [];
  return (
    <div>
      <PageHeader
        title="Investigation Prompts Queue"
        description="Dev-loop investigation prompts queued for operator review. Copy the prompt to clipboard and paste into Claude Code; the auto-execute action is deferred to a future iteration."
      />
      {prompts.length === 0 ? (
        <EmptyState
          title="No pending prompts"
          description="The dev-loop has not surfaced any investigation prompts. Prompts appear when validators detect anomalies that warrant operator review."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {prompts.map((p) => (
            <PromptCard
              key={p.id}
              prompt={p}
              onMark={(status) => markReviewed.mutate({ id: p.id, status })}
              pending={markReviewed.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PromptCard({
  prompt,
  onMark,
  pending,
}: {
  prompt: InvestigationPrompt;
  onMark: (status: "reviewed" | "discarded") => void;
  pending: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prompt.claudePrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be blocked by browser permissions — leave state as-is.
    }
  };
  return (
    <Card>
      <SectionHeader
        title={prompt.description}
        accessory={
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Badge variant={SEVERITY_VARIANT[prompt.severity]}>{prompt.severity}</Badge>
            <Badge variant="info">{prompt.component}</Badge>
            <Badge variant="info">{prompt.deploymentId}</Badge>
          </div>
        }
      />
      <pre
        style={{
          marginTop: "0.75rem",
          padding: "0.75rem",
          background: "var(--c-bg-elev-1)",
          borderRadius: "4px",
          fontSize: "0.85em",
          maxHeight: "20rem",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
        }}
      >
        {prompt.claudePrompt}
      </pre>
      {prompt.filePaths.length > 0 && (
        <div style={{ marginTop: "0.5rem" }}>
          <strong>Files:</strong>
          <ul style={{ marginTop: "0.25rem", marginLeft: "1.5rem" }}>
            {prompt.filePaths.map((fp) => (
              <li key={fp}>
                <code>{fp}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
      {prompt.testCommand && (
        <div style={{ marginTop: "0.5rem" }}>
          <strong>Verify with:</strong>
          <pre
            style={{
              marginTop: "0.25rem",
              padding: "0.5rem",
              background: "var(--c-bg-elev-1)",
              borderRadius: "4px",
              fontSize: "0.85em",
            }}
          >
            {prompt.testCommand}
          </pre>
        </div>
      )}
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
        <Button onClick={onCopy} variant="primary">
          {copied ? "Copied!" : "Copy prompt"}
        </Button>
        <Button
          onClick={() => onMark("reviewed")}
          variant="secondary"
          disabled={pending}
        >
          Mark reviewed
        </Button>
        <Button
          onClick={() => onMark("discarded")}
          variant="ghost"
          disabled={pending}
        >
          Discard
        </Button>
      </div>
    </Card>
  );
}
